/* eslint-disable no-nested-ternary */
import React, { useEffect, useRef, useState } from 'react';
import {
  Typography,
  Chip,
  Box,
  IconButton,
  Switch,
  CircularProgress,
  Tooltip,
  styled,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { tooltipClasses } from '@mui/material/Tooltip';
import PropTypes from 'prop-types';
import parse from 'html-react-parser';
import CELL_DATA_TYPES from '../../config/cell-types.config';
import {
  formatNumber,
  formatPercent,
  formatDuration,
  formatCurrency,
} from '../../utils/numbers.util';
import { formatDate, formatTimestamp } from '../../utils/datetime.util';
import StatusLabel from './status-label.component';
import PlatformLabel from './platform-label.component';
import Iconify from '../common/iconify.component';
import {
  APP_THEME,
  ERROR,
  GREY,
  PRIMARY,
  SUCCESS,
  WARNING,
} from '../../theme/theme-colors';
import RoleLabel from './role-label.component';
import {
  CATEGORY_TYPE,
  QUESTION_TYPES,
  VIDEO_TYPES,
} from '../../config/const.config';
import RecurringStatus from '../content/recurring-event-status.component';
import ListMatchOptions from '../quiz/list-match-options.component';
import HtmlRunner from '../common/htmlrunner';
import handleHTMLContent from '../../utils/html-parser.util';

const categoryTypes = Object.values([...CATEGORY_TYPE]).map((el) => {
  const element = el;
  element.value = element.id;
  return element;
});

const videoTypes = Object.values(VIDEO_TYPES);

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} placement="top" />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 300,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}));

const CellValue = ({
  type,
  value,
  duration,
  fallbackValue,
  cdnStatus,
  disabledToggle,
  hasSubRows,
  handleShowSubList,
  isRecurring,
  primaryProfileUser,
  requestedProfileUser,
  quizQuestionType,
  quizPassingCriteria,
  totalQuizQuestions,
  userQuizAnswer,
  quizMatchOptions,
  userOptions,
  quizQuestionExplanation,
  dataKey,
}) => {
  const [open, setOpen] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');
  const handleLongDescription = (html) => {
    setHtmlContent(html);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setHtmlContent('');
  };
  if (type === CELL_DATA_TYPES.NUMBER) {
    return (
      <Typography variant="body2" component="span">
        {formatNumber(value || 0)}
      </Typography>
    );
  }

  if (
    type === CELL_DATA_TYPES.REQUEST &&
    primaryProfileUser &&
    requestedProfileUser &&
    primaryProfileUser !== requestedProfileUser
  ) {
    return `${value} *`;
  }

  if (type === CELL_DATA_TYPES.PERCENTAGE) {
    return (
      <Typography variant="body2" component="span">
        {formatPercent(value || 0)}
      </Typography>
    );
  }

  if (type === CELL_DATA_TYPES.PROGRESS) {
    return (
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <CircularProgress
          variant="determinate"
          value={value}
          thickness={4}
          size={48}
        />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="caption"
            component="div"
            sx={{ color: 'text.primary' }}
          >
            {`${Math.round(value || 0)}%`}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (type === CELL_DATA_TYPES.MONEY) {
    return (
      <Typography variant="body2" component="span">
        CA${formatCurrency(value || 0)}
      </Typography>
    );
  }

  if (type === CELL_DATA_TYPES.DURATION) {
    return (
      <Typography variant="body2" component="span">
        {formatDuration(value || 0)}
      </Typography>
    );
  }

  if (type === CELL_DATA_TYPES.DATE) {
    return (
      <Typography variant="body2" component="span">
        {formatDate(value || null, 'll')}
      </Typography>
    );
  }

  if (type === CELL_DATA_TYPES.DATETIME) {
    return (
      <Typography variant="body2" component="span">
        {formatDate(value || null, 'lll')}
      </Typography>
    );
  }

  if (type === CELL_DATA_TYPES.TIMESTAMP) {
    return (
      <Typography variant="body2" component="span">
        {formatTimestamp(value || null, 'lll')}
      </Typography>
    );
  }

  if (type === CELL_DATA_TYPES.BOOL) {
    return (
      <Chip
        label={Number(value) === 1 ? 'Yes' : 'No'}
        color={Number(value) === 1 ? 'success' : 'error'}
        variant="filled"
        size="small"
      />
    );
  }

  if (type === CELL_DATA_TYPES.PLATFORM) {
    return <PlatformLabel value={value} />;
  }

  if (type === CELL_DATA_TYPES.ROLE) {
    return <RoleLabel value={value} />;
  }

  if (type === CELL_DATA_TYPES.SWITCH) {
    return (
      <Switch
        aria-label="status"
        color="primary"
        defaultChecked={!!value}
        onChange={(e) => fallbackValue(e)}
        disabled={disabledToggle}
      />
    );
  }

  if (type === CELL_DATA_TYPES.STATUS) {
    return <StatusLabel value={value} />;
  }

  if (type === CELL_DATA_TYPES.CODE) {
    return (
      <Box component="span" sx={{ display: 'block' }}>
        {value || ''}
      </Box>
    );
  }

  if (type === CELL_DATA_TYPES.PREVIEW) {
    return (
      <IconButton
        aria-label="preview"
        disabled={cdnStatus !== 'Finished'}
        sx={{ padding: 0, color: APP_THEME }}
      >
        <Iconify icon="solar:play-bold" />
      </IconButton>
    );
  }

  if (type === CELL_DATA_TYPES.RECORDED_PREVIEW) {
    return (
      <IconButton
        aria-label="recordedPreview"
        sx={{ padding: 0, color: APP_THEME }}
      >
        <Iconify icon="solar:play-bold" />
      </IconButton>
    );
  }

  if (type === CELL_DATA_TYPES.IMAGE) {
    if (value === null || value === '') {
      return (
        <Box
          sx={{
            height: 54,
            width: 96,
            bgcolor: '#808080',
          }}
        />
      );
    }

    return (
      <Box
        component="img"
        sx={{
          height: 54,
          width: 96,
          objectFit: 'contain',
        }}
        src={value}
      />
    );
  }

  if (type === CELL_DATA_TYPES.PROFILE) {
    if (value === null || value === '') {
      return (
        <Box
          sx={{
            height: 36,
            width: 36,
            bgcolor: '#808080',
            borderRadius: 18,
          }}
        />
      );
    }

    return (
      <Box
        component="img"
        sx={{
          height: 36,
          width: 36,
          objectFit: 'cover',
          borderRadius: 18,
        }}
        src={value}
      />
    );
  }

  if (type === CELL_DATA_TYPES.UCWORDS) {
    return (
      <Box component="span" sx={{ display: 'block' }}>
        {
          [...categoryTypes, ...videoTypes].find((el) => el.value === value)
            .label
        }
        {isRecurring === 1 ? ' (R)' : ''}
      </Box>
    );
  }

  if (type === CELL_DATA_TYPES.SUB_LIST) {
    if (!value) {
      return '';
    }

    return (
      <IconButton
        aria-label="arrow"
        sx={{ color: APP_THEME }}
        onClick={handleShowSubList}
      >
        <Iconify
          icon={hasSubRows ? 'iconamoon:arrow-up-2' : 'iconamoon:arrow-down-2'}
        />
      </IconButton>
    );
  }

  if (type === CELL_DATA_TYPES.EVENT_TIMESTAMP) {
    return <RecurringStatus recurringTimestamp={value} duration={duration} />;
  }

  if (type === CELL_DATA_TYPES.QUIZ_RESULT_PERCENT_PF) {
    return (
      <>
        <Typography variant="body2" sx={{ marginBottom: 1 }}>
          {formatPercent(value || 0)}
        </Typography>

        <Chip
          label={Number(value) > Number(quizPassingCriteria) ? 'Pass' : 'Fail'}
          color={
            Number(value) > Number(quizPassingCriteria) ? 'success' : 'error'
          }
          variant="filled"
          size="small"
          sx={{ height: '16px' }}
        />
      </>
    );
  }

  if (type === CELL_DATA_TYPES.QUIZ_RESULT_MARKS_OUT_OF) {
    return (
      <Typography variant="body2" sx={{ marginBottom: 1 }}>
        {formatNumber(value || 0)} / {formatNumber(totalQuizQuestions || 0)}
      </Typography>
    );
  }

  if (type === CELL_DATA_TYPES.QUIZ_ATTEMPT) {
    let attemptType;
    if (value === 1) {
      attemptType = { icon: 'mdi:tick-circle', color: SUCCESS.main };
    } else if (value === -1) {
      attemptType = { icon: 'lsicon:minus-filled', color: WARNING.main };
    } else {
      attemptType = { icon: 'material-symbols:cancel', color: ERROR.main };
    }

    return (
      <HtmlTooltip
        title={
          quizQuestionType === QUESTION_TYPES.MATCH.value ? (
            userOptions.length > 0 ? (
              <div style={{ fontWeight: 500, fontSize: 12 }}>
                <b>Answer given: </b>
                <ListMatchOptions options={userOptions} fontSize={12} />
              </div>
            ) : null
          ) : userQuizAnswer ? (
            <div style={{ fontWeight: 500, fontSize: 12 }}>
              <b>Answer given: </b>
              {userQuizAnswer}
            </div>
          ) : null
        }
        disableHoverListener={value === 1}
      >
        <Iconify
          icon={attemptType.icon}
          sx={{ color: attemptType.color }}
          width={25}
        />
      </HtmlTooltip>
    );
  }

  if (type === CELL_DATA_TYPES.QUIZ_ANSWER) {
    const [expanded, setExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const textRef = useRef(null);

    useEffect(() => {
      const el = textRef.current;
      if (el) {
        const isOver = el.scrollHeight > el.clientHeight + 1;
        setIsOverflowing(isOver);
      }
    }, []);

    return (
      <Box>
        {quizQuestionType === QUESTION_TYPES.MATCH.value ? (
          <ListMatchOptions options={quizMatchOptions} />
        ) : (
          <Typography
            color="inherit"
            sx={{ fontWeight: 700, fontSize: 14, marginBottom: 0.5 }}
          >
            {value}
          </Typography>
        )}

        {quizQuestionExplanation ? (
          <Typography
            ref={textRef}
            variant="body1"
            sx={{
              fontSize: 12,
              display: '-webkit-box',
              WebkitLineClamp: expanded ? 'unset' : 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <b>Expl: </b>
            {quizQuestionExplanation}
          </Typography>
        ) : null}

        {isOverflowing && (
          <Button
            onClick={() => setExpanded(!expanded)}
            sx={{
              display: 'flex',
              justifySelf: 'end',
              fontSize: '11px',
              padding: '4px 6px',
            }}
          >
            {expanded ? 'View Less' : 'View More'}
          </Button>
        )}
      </Box>
    );
  }
  if (
    type === CELL_DATA_TYPES.LONG_DESCRIPTION ||
    type === CELL_DATA_TYPES.BIO
  ) {
    return (
      <>
        <Button
          onClick={() => handleLongDescription(value)}
          sx={{
            color: GREY[0],
            display: 'flex',
            justifySelf: 'start',
            fontSize: '16px',
            padding: '4px 6px',
            backgroundColor: APP_THEME,
            '&:hover': {
              backgroundColor: PRIMARY.dark,
            },
          }}
        >
          Preview
        </Button>

        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
          <DialogTitle>
            {type.charAt(0).toUpperCase() + type.slice(1)} Preview - {dataKey}
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            size="small"
            sx={{
              position: 'absolute',
              right: 10,
              top: 15,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Iconify icon="ic:outline-close" />
          </IconButton>
          <DialogContent dividers>
            <HtmlRunner html={htmlContent} />
          </DialogContent>
        </Dialog>
      </>
    );
  }

  if (type === CELL_DATA_TYPES.CURRENT_DATA_NAME) {
    const [expanded, setExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const textRef = useRef(null);

    useEffect(() => {
      const el = textRef.current;
      if (el) {
        if (expanded) {
          setIsOverflowing(true);
        } else {
          const isOver = el.scrollHeight > el.clientHeight + 1;
          setIsOverflowing(isOver);
        }
      }
    }, [value, expanded]);

    return (
      <Box>
        <Typography
          ref={textRef}
          variant="body2"
          sx={{
            fontSize: 13,
            whiteSpace: 'pre-line',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: expanded ? 'unset' : 4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {parse(handleHTMLContent(value))}
        </Typography>

        {isOverflowing && (
          <Button
            onClick={() => setExpanded(!expanded)}
            sx={{
              fontSize: 11,
              textTransform: 'none',
              padding: '2px 4px 2px 0px',
            }}
          >
            {expanded ? 'Read Less' : 'Read More'}
          </Button>
        )}
      </Box>
    );
  }

  if (type === CELL_DATA_TYPES.TEXT || type === CELL_DATA_TYPES.DESCRIPTION) {
    const [expanded, setExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const textRef = useRef(null);

    useEffect(() => {
      const el = textRef.current;
      if (el) {
        if (expanded) {
          setIsOverflowing(true);
        } else {
          const isOver = el.scrollHeight > el.clientHeight + 1;
          setIsOverflowing(isOver);
        }
      }
    }, [value, expanded]);

    return (
      <Box>
        <Typography
          ref={textRef}
          variant="body2"
          sx={{
            fontSize: 13,
            whiteSpace: 'pre-line',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: expanded ? 'unset' : 4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {value}
        </Typography>

        {isOverflowing && (
          <Button
            onClick={() => setExpanded(!expanded)}
            sx={{
              fontSize: 11,
              textTransform: 'none',
              padding: '2px 4px 2px 0px',
            }}
          >
            {expanded ? 'Read Less' : 'Read More'}
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Typography
      variant="body2"
      component="span"
      sx={{ whiteSpace: 'pre-line' }}
    >
      {value || ''}
    </Typography>
  );
};

CellValue.propTypes = {
  type: PropTypes.string.isRequired,
  value: PropTypes.any,
  duration: PropTypes.number,
  fallbackValue: PropTypes.func,
  cdnStatus: PropTypes.string,
  disabledToggle: PropTypes.bool,
  hasSubRows: PropTypes.bool,
  handleShowSubList: PropTypes.func,
  isRecurring: PropTypes.bool,
  primaryProfileUser: PropTypes.number,
  requestedProfileUser: PropTypes.number,
  quizQuestionType: PropTypes.number,
  quizPassingCriteria: PropTypes.number,
  totalQuizQuestions: PropTypes.number,
  userQuizAnswer: PropTypes.string,
  quizMatchOptions: PropTypes.array,
  userOptions: PropTypes.array,
  quizQuestionExplanation: PropTypes.string,
  dataKey: PropTypes.string,
};
CellValue.defaultProps = {
  value: null,
  duration: 0,
  isRecurring: 0,
  primaryProfileUser: 0,
  requestedProfileUser: 0,
  quizQuestionType: null,
  quizPassingCriteria: 0,
  totalQuizQuestions: 0,
  userQuizAnswer: '',
  quizMatchOptions: [],
  userOptions: [],
  quizQuestionExplanation: '',
  fallbackValue: () => {},
  cdnStatus: null,
  disabledToggle: false,
  hasSubRows: false,
  handleShowSubList: () => {},
  dataKey: '',
};

export default CellValue;
